/**
 * DServer API Service
 *
 * This module provides a singleton instance of DServerClient configured
 * for use throughout the webapp. It wraps the dserver-client-js library.
 */
import { DServerClient } from "dserver-client";
import { serverUrl } from "@/config";
// Re-export error classes
export { DServerError as DServerErrorClass, AuthenticationError as AuthenticationErrorClass, AuthorizationError as AuthorizationErrorClass, NotFoundError as NotFoundErrorClass, } from "dserver-client";
/**
 * DServer API wrapper class
 * Provides methods matching the webapp's current usage patterns
 */
class DServerApi {
    constructor() {
        Object.defineProperty(this, "client", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "baseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.baseUrl = serverUrl;
    }
    /**
     * Initialize or update the client with a token
     */
    setToken(token) {
        if (!this.client) {
            this.client = new DServerClient({
                baseUrl: this.baseUrl,
                token,
            });
        }
        else {
            this.client.setToken(token);
        }
    }
    /**
     * Get the current base URL
     */
    getBaseUrl() {
        return this.baseUrl;
    }
    /**
     * Ensure client is initialized
     */
    ensureClient() {
        if (!this.client) {
            throw new Error("DServer client not initialized. Call setToken() first.");
        }
        return this.client;
    }
    // =========================================================================
    // Config & Health
    // =========================================================================
    async getServerVersions() {
        return this.ensureClient().getServerVersions();
    }
    async checkHealth() {
        return this.ensureClient().checkHealth();
    }
    // =========================================================================
    // Search & Summary
    // =========================================================================
    async searchDatasets(query, pagination) {
        return this.ensureClient().searchDatasets(query, pagination);
    }
    async getUserSummary(username) {
        return this.ensureClient().getUserSummary(username);
    }
    // =========================================================================
    // Dataset Details
    // =========================================================================
    async getManifest(uri) {
        return this.ensureClient().getManifest(uri);
    }
    async getReadme(uri) {
        return this.ensureClient().getReadme(uri);
    }
    // =========================================================================
    // Tags
    // =========================================================================
    async getTags(uri) {
        return this.ensureClient().getTags(uri);
    }
    async setTags(uri, tags) {
        return this.ensureClient().setTags(uri, tags);
    }
    async addTag(uri, tag) {
        return this.ensureClient().addTag(uri, tag);
    }
    async removeTag(uri, tag) {
        return this.ensureClient().removeTag(uri, tag);
    }
    // =========================================================================
    // Annotations
    // =========================================================================
    async getAnnotations(uri) {
        return this.ensureClient().getAnnotations(uri);
    }
    async setAnnotations(uri, annotations) {
        return this.ensureClient().setAnnotations(uri, annotations);
    }
    async setAnnotation(uri, name, value) {
        return this.ensureClient().setAnnotation(uri, name, value);
    }
    async deleteAnnotation(uri, name) {
        return this.ensureClient().deleteAnnotation(uri, name);
    }
    // =========================================================================
    // README
    // =========================================================================
    async setReadme(uri, content) {
        return this.ensureClient().setReadme(uri, content);
    }
    // =========================================================================
    // Signed URLs
    // =========================================================================
    async getItemSignedUrl(uri, identifier) {
        return this.ensureClient().getItemSignedUrl(uri, identifier);
    }
    // =========================================================================
    // User Management (Admin only)
    // =========================================================================
    async getCurrentUser() {
        return this.ensureClient().getCurrentUser();
    }
    async listUsers() {
        return this.ensureClient().listUsers();
    }
    async getUser(username) {
        return this.ensureClient().getUser(username);
    }
    async createUser(username, options) {
        return this.ensureClient().createUser(username, options);
    }
    async deleteUser(username) {
        return this.ensureClient().deleteUser(username);
    }
    async updateUserAdmin(username, isAdmin) {
        return this.ensureClient().updateUserAdmin(username, isAdmin);
    }
    async updateUser(username, updates) {
        return this.ensureClient().updateUser(username, updates);
    }
    // =========================================================================
    // Base URI Management (Admin only)
    // =========================================================================
    async listBaseURIs() {
        return this.ensureClient().listBaseURIs();
    }
    async getBaseURI(baseUri) {
        return this.ensureClient().getBaseURI(baseUri);
    }
    async createBaseURI(baseUri) {
        return this.ensureClient().createBaseURI(baseUri);
    }
    async deleteBaseURI(baseUri) {
        return this.ensureClient().deleteBaseURI(baseUri);
    }
    async grantSearchPermission(username, baseUri) {
        return this.ensureClient().grantSearchPermission(username, baseUri);
    }
    async revokeSearchPermission(username, baseUri) {
        return this.ensureClient().revokeSearchPermission(username, baseUri);
    }
    async grantRegisterPermission(username, baseUri) {
        return this.ensureClient().grantRegisterPermission(username, baseUri);
    }
    async revokeRegisterPermission(username, baseUri) {
        return this.ensureClient().revokeRegisterPermission(username, baseUri);
    }
    // =========================================================================
    // Dependency Graph (requires dserver-dependency-graph-plugin)
    // =========================================================================
    async getDependencyGraph(uuid, dependencyKeys) {
        return this.ensureClient().getDependencyGraph(uuid, dependencyKeys);
    }
}
// Export singleton instance
export const dserverApi = new DServerApi();
// Export the class for testing purposes
export { DServerApi };
